import AppConfig from "@/core/config/AppConfig";
import store from "@/store";
import { Mutations } from "@/store/enums/StoreEnums";
import JwtService from "../JwtService";
import BaseApiService from "./BaseApiService";
import ServiceConstants from "./ServiceConstants";
import Pagination from '@/models/common/Pagination'
import NotificationDialog from "@/core/helpers/notificationDialog";

class IdentityApiService extends BaseApiService {
    private static client = new BaseApiService()
        .Build(AppConfig.Api.Identity.BaseUrl);
    private static authenticatedClient = () => {
        const token = JwtService.getToken()
        return new BaseApiService(token)
            .Build(AppConfig.Api.Identity.BaseUrl);
    }

    public static Membership = {
        Claim: {
            GetListPaginated: function (pagination: Pagination) {
                return BaseApiService.GetList(IdentityApiService.authenticatedClient(), ServiceConstants.Identity.Membership.Claim.ClaimListPath(pagination.page!, pagination.pageSize))
            },
            GetById: function (id: string) {
                return BaseApiService.GetById(IdentityApiService.authenticatedClient(), ServiceConstants.Identity.Membership.Claim.ClaimDetailPath(id))
            },
            Create: function (credentials: any) {
                return BaseApiService.Create(IdentityApiService.authenticatedClient(), ServiceConstants.Identity.Membership.Claim.Name, credentials)
            },
            Update: function (credentials: any) {
                return BaseApiService.Update(IdentityApiService.authenticatedClient(), ServiceConstants.Identity.Membership.Claim.ClaimDetailPath(credentials.id), credentials)
            },
            Delete: function (id: string) {
                return BaseApiService.Delete(IdentityApiService.authenticatedClient(), ServiceConstants.Identity.Membership.Claim.ClaimDetailPath(id))
            }
        },
        Role: {
            GetListPaginated: function (pagination: Pagination, filters: any) {
                return BaseApiService.GetList(IdentityApiService.authenticatedClient(), ServiceConstants.Identity.Membership.Role.RoleListPath(pagination.page!, pagination.pageSize, filters))
            },
            GetById: function (id: string) {
                return BaseApiService.GetById(IdentityApiService.authenticatedClient(), ServiceConstants.Identity.Membership.Role.RoleDetailPath(id))
            },
            GetByIdWithClaims: function (id: string) {
                return BaseApiService.GetById(IdentityApiService.authenticatedClient(), `${ServiceConstants.Identity.Membership.Role.RoleDetailPath(id)}/withClaims`)
            },
            Create: function (credentials: any) {
                return BaseApiService.Create(IdentityApiService.authenticatedClient(), ServiceConstants.Identity.Membership.Role.Name, credentials).then(({ data }) => {
                    if (data.data) {
                        NotificationDialog.SuccessPrompt("roleList", "editRole", data.data.id)
                    }
                })
            },
            Update: function (credentials: any) {
                return BaseApiService.Update(IdentityApiService.authenticatedClient(), ServiceConstants.Identity.Membership.Role.RoleDetailPath(credentials.id), credentials)
            },
            UpdateClaims: function (id, claims: Array<any>) {
                return BaseApiService.Update(IdentityApiService.authenticatedClient(), `${ServiceConstants.Identity.Membership.Role.RoleDetailPath(id)}/claims`, claims)
            },
            Delete: function (id: string) {
                return BaseApiService.Delete(IdentityApiService.authenticatedClient(), ServiceConstants.Identity.Membership.Role.RoleDetailPath(id))
            },
        },
        User: {
            GetForSelect: function () {
                return BaseApiService.GetListForSelectbox(IdentityApiService.authenticatedClient(), ServiceConstants.Identity.Membership.User.UserListPath(1, 1000, null));
            },
            GetListPaginated: function (pagination: Pagination, filters: any) {
                return BaseApiService.GetList(IdentityApiService.authenticatedClient(), ServiceConstants.Identity.Membership.User.UserListPath(pagination.page!, pagination.pageSize, filters))
            },
            GetById: function (id: string) {
                return BaseApiService.GetById(IdentityApiService.authenticatedClient(), ServiceConstants.Identity.Membership.User.UserDetailPath(id))
            },
            GetByIdWithRoles: function (id: string) {
                return BaseApiService.GetById(IdentityApiService.authenticatedClient(), `${ServiceConstants.Identity.Membership.User.UserDetailPath(id)}/withRoles`)
            },
            GetByIdWithClaims: function (id: string) {
                return BaseApiService.GetById(IdentityApiService.authenticatedClient(), `${ServiceConstants.Identity.Membership.User.UserDetailPath(id)}/withClaims`)
            },
            Create: function (credentials: any) {
                return BaseApiService.Create(IdentityApiService.authenticatedClient(), ServiceConstants.Identity.Membership.User.Name, credentials)
            },
            CreateWithRobot: function (credentials: any) {
                return BaseApiService.Create(IdentityApiService.authenticatedClient(), `${ServiceConstants.Identity.Membership.User.Name}/withRobot`, credentials)
            },
            CreateWithStudioRobot: function (credentials: any) {
                return BaseApiService.Create(IdentityApiService.authenticatedClient(), `${ServiceConstants.Identity.Membership.User.Name}/withStudioRobot`, credentials)
            },
            Update: function (credentials: any) {
                return BaseApiService.Update(IdentityApiService.authenticatedClient(), ServiceConstants.Identity.Membership.User.UserDetailPath(credentials.id), credentials).then(() => {
                    NotificationDialog.Notify("Updateduccessfully")
                })
            },
            UpdateRoles: function (id: string, roles: Array<any>) {
                return BaseApiService.Update(IdentityApiService.authenticatedClient(), `${ServiceConstants.Identity.Membership.User.UserDetailPath(id)}/roles`, roles).then(() => {
                    NotificationDialog.Notify("UpdatedSuccessfully")
                })
            },
            UpdateClaims: function (id: string, claims: Array<any>) {
                return BaseApiService.Update(IdentityApiService.authenticatedClient(), `${ServiceConstants.Identity.Membership.User.UserDetailPath(id)}/claims`, claims).then(() => {
                    NotificationDialog.Notify("UpdatedSuccessfully")
                })
            },
            Delete: function (id: string) {
                return BaseApiService.Delete(IdentityApiService.authenticatedClient(), ServiceConstants.Identity.Membership.User.UserDetailPath(id))
            },
            ChangeLanguage: function (langCode: string) {
                return BaseApiService.Update(IdentityApiService.authenticatedClient(), `${ServiceConstants.Identity.Membership.User.Name}/changeLanguage/${langCode}`, {})
            }
        },
        Auth: {
            VerifyAuthCode: function (payload: any) {
                return BaseApiService.Create(IdentityApiService.client, ServiceConstants.Identity.Membership.Auth.VerifyAuthPath, payload)
            },
            Login: function (credentials: any) {
                return BaseApiService.Create(IdentityApiService.client, ServiceConstants.Identity.Membership.Auth.LoginPath, credentials)
            },
            Logout: function (id: string) {
                return IdentityApiService.authenticatedClient().put(ServiceConstants.Identity.Membership.Auth.LogoutPath(id), {})
            },
            Verify: function (token: string) {
                return IdentityApiService.client.post(ServiceConstants.Identity.Membership.Auth.VerifyPath, { token: token })
                    .then(({ data }) => {
                        if (data.hasFailed) {
                            store.commit(Mutations.PURGE_AUTH, true);
                        }
                    })
                    .catch((err) => {
                        store.commit(Mutations.PURGE_AUTH, true);
                    })
            },
            ChangePassword: function (credentials: any) {
                return BaseApiService.Create(IdentityApiService.authenticatedClient(), ServiceConstants.Identity.Membership.Auth.ChangePasswordPath, credentials)
            },
            ForgotPassword: function (credentials: any) {
                return BaseApiService.Create(IdentityApiService.client, ServiceConstants.Identity.Membership.Auth.ForgotPasswordPath, credentials)
            },
            ResetPassword: function (credentials: any) {
                return BaseApiService.Create(IdentityApiService.client, ServiceConstants.Identity.Membership.Auth.ResetPasswordPath, credentials)
            }
        },
    }
}

export default IdentityApiService;